import { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import {
  getCurrentShop,
  getCurrentUser,
  hasPermission,
  isOwnerInShopMode,
} from "@/lib/auth";
import { getShopUsers, createUser, updateUser, deleteUser } from "@/lib/api";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
  DialogFooter,
} from "@/components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Badge } from "@/components/ui/badge";
import {
  Loader2,
  Plus,
  Edit2,
  Trash2,
  UserPlus,
  Shield,
  UserCog,
} from "lucide-react";
import { toast } from "@/hooks/use-toast";
import { usePlan } from "@/hooks/usePlan";

export default function ShopUsers() {
  const currentShop = getCurrentShop();
  const currentUser = getCurrentUser();
  const queryClient = useQueryClient();
  const [showAddDialog, setShowAddDialog] = useState(false);
  const [editingUser, setEditingUser] = useState<any>(null);

  // ============ VERIFICAR SI ES OWNER EN MODO TIENDA ============
  const isOwnerMode = isOwnerInShopMode();

  // Log para depuración
  console.log("👑 ShopUsers - ¿Es owner en modo tienda?", isOwnerMode);

  const { data: users = [], isLoading } = useQuery({
    queryKey: ["shopUsers", currentShop?.id],
    queryFn: () => getShopUsers(currentShop!.id),
    enabled: !!currentShop && hasPermission("store_manager"),
  });

  // Obtener información del plan
  const { can, planLimits, planName } = usePlan();

  // Calcular si puede crear más usuarios
  const canCreateUser =
    users.length <
    (planLimits.maxUsersPerShop === Infinity
      ? Infinity
      : planLimits.maxUsersPerShop);
  const usersRemaining =
    planLimits.maxUsersPerShop === Infinity
      ? "∞"
      : Math.max(0, planLimits.maxUsersPerShop - users.length);

  // ============ VERIFICAR ACCESO: OWNER TIENE ACCESO TOTAL ============
  if (!currentShop) {
    return (
      <div className="container mx-auto px-4 py-8">
        <Card>
          <CardContent className="pt-6 text-center">
            <Shield className="h-12 w-12 mx-auto text-muted-foreground mb-4" />
            <h3 className="text-lg font-semibold mb-2">Tienda no encontrada</h3>
            <p className="text-muted-foreground">
              No se pudo cargar la información de la tienda.
            </p>
          </CardContent>
        </Card>
      </div>
    );
  }

  // Si es owner, tiene acceso total aunque no tenga permiso de manager
  if (!isOwnerMode && !hasPermission("store_manager")) {
    return (
      <div className="container mx-auto px-4 py-8">
        <Card>
          <CardContent className="pt-6 text-center">
            <Shield className="h-12 w-12 mx-auto text-muted-foreground mb-4" />
            <h3 className="text-lg font-semibold mb-2">Acceso restringido</h3>
            <p className="text-muted-foreground">
              Solo administradores y managers pueden gestionar usuarios.
            </p>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="container mx-auto px-4 py-8">
      <div className="mb-8 space-y-4">
        <h1 className="text-3xl font-heading font-bold uppercase text-center sm:text-left">
          Gestión de Usuarios
        </h1>
        <p className="text-muted-foreground text-center sm:text-left">
          Administra los usuarios de tu tienda: vendedores, administradores y
          managers
        </p>
      </div>

      {/* Tarjeta de resumen */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-muted-foreground">Total Usuarios</p>
                <p className="text-2xl font-bold">
                  {users.length}
                  <span className="text-sm font-normal text-muted-foreground ml-2">
                    /{" "}
                    {planLimits.maxUsersPerShop === Infinity
                      ? "∞"
                      : planLimits.maxUsersPerShop}
                  </span>
                </p>
                {planLimits.maxUsersPerShop !== Infinity && (
                  <p className="text-xs text-muted-foreground mt-1">
                    {usersRemaining === 0
                      ? "Límite alcanzado"
                      : `${usersRemaining} restante${usersRemaining !== 1 ? "s" : ""}`}
                  </p>
                )}
              </div>
              <div className="p-3 bg-primary/10 rounded-full">
                <UserCog className="h-6 w-6 text-primary" />
              </div>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-muted-foreground">Administradores</p>
                <p className="text-2xl font-bold">
                  {users.filter((u) => u.role === "supervisor").length}
                </p>
              </div>
              <div className="p-3 bg-red-100 rounded-full">
                <Shield className="h-6 w-6 text-red-600" />
              </div>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-muted-foreground">Activos</p>
                <p className="text-2xl font-bold">
                  {users.filter((u) => u.isActive).length}/{users.length}
                </p>
              </div>
              <div className="p-3 bg-green-100 rounded-full">
                <UserPlus className="h-6 w-6 text-green-600" />
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Botón para agregar usuario */}
      <div className="flex justify-end mb-6">
        <div className="relative group">
          <Button
            onClick={() => setShowAddDialog(true)}
            className="gap-2"
            disabled={!canCreateUser}
          >
            <Plus className="h-4 w-4" />
            Agregar Usuario
          </Button>
          {!canCreateUser && planLimits.maxUsersPerShop !== Infinity && (
            <div className="absolute right-0 mt-2 w-64 p-2 bg-amber-100 border border-amber-300 rounded-md text-xs text-amber-800 shadow-lg z-50 hidden group-hover:block">
              <p className="font-medium">⚠️ Límite alcanzado</p>
              <p>
                Has alcanzado el máximo de {planLimits.maxUsersPerShop} usuarios
                para tu plan {planName}.
              </p>
              <p className="mt-1 text-amber-700">
                Mejora tu plan para agregar más usuarios.
              </p>
            </div>
          )}
        </div>
      </div>

      {/* Tabla de usuarios */}
      <Card>
        <CardContent className="pt-6">
          {isLoading ? (
            <div className="flex justify-center items-center py-20">
              <Loader2 className="h-12 w-12 animate-spin text-primary" />
            </div>
          ) : users.length === 0 ? (
            <div className="text-center py-12 text-muted-foreground">
              <UserCog className="h-12 w-12 mx-auto mb-4 opacity-20" />
              <p className="text-lg">No hay usuarios registrados</p>
              <p className="text-sm">Agrega tu primer usuario para comenzar</p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Usuario</TableHead>
                    <TableHead>Nombre</TableHead>
                    <TableHead>Rol</TableHead>
                    <TableHead>Estado</TableHead>
                    <TableHead className="text-right">Acciones</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {users.map((user) => (
                    <TableRow key={user.id}>
                      <TableCell className="font-medium">
                        {user.username}
                      </TableCell>
                      <TableCell>{user.fullName}</TableCell>
                      <TableCell>
                        <Badge
                          variant={
                            user.role === "supervisor"
                              ? "destructive"
                              : user.role === "store_manager"
                                ? "default"
                                : "secondary"
                          }
                        >
                          {user.role === "supervisor"
                            ? "Supervisor"
                            : user.role === "store_manager"
                              ? "Store Manager"
                              : "Vendedor"}
                        </Badge>
                      </TableCell>
                      <TableCell>
                        <Badge variant={user.isActive ? "default" : "outline"}>
                          {user.isActive ? "Activo" : "Inactivo"}
                        </Badge>
                      </TableCell>
                      <TableCell className="text-right">
                        <div className="flex justify-end gap-2">
                          <Button
                            variant="ghost"
                            size="icon"
                            onClick={() => setEditingUser(user)}
                            // Owner puede editar cualquier usuario, incluyéndose a sí mismo
                            disabled={
                              !isOwnerMode && user.id === currentUser?.id
                            }
                          >
                            <Edit2 className="h-4 w-4" />
                          </Button>
                          <Button
                            variant="ghost"
                            size="icon"
                            className="text-destructive hover:text-destructive"
                            onClick={() => handleDeleteUser(user.id)}
                            // Owner puede eliminar cualquier usuario, excepto a sí mismo
                            disabled={
                              !isOwnerMode && user.id === currentUser?.id
                            }
                          >
                            <Trash2 className="h-4 w-4" />
                          </Button>
                        </div>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Diálogo para agregar/editar usuario */}
        <UserDialog
          key={editingUser?.id ?? "create"}
          open={showAddDialog || !!editingUser}
          onOpenChange={(open) => {
            if (!open) {
              setShowAddDialog(false);
              setEditingUser(null);
            }
          }}
          user={editingUser}
          shopId={currentShop.id}
        />
    </div>
  );

  async function handleDeleteUser(userId: string) {
    // ============ PREGUNTAR ANTES DE ELIMINAR ============
    if (
      !confirm(
        "¿Estás seguro de eliminar este usuario? Esta acción no se puede deshacer.",
      )
    )
      return;

    try {
      await deleteUser(currentShop!.id, userId);
      toast({
        title: "Usuario eliminado",
        description: "El usuario ha sido eliminado correctamente.",
      });
      queryClient.invalidateQueries({
        queryKey: ["shopUsers", currentShop?.id],
      });
    } catch (error: any) {
      console.error("Error eliminando usuario:", error);

      // Mensaje de error más específico
      const errorMessage = error.message || "No se pudo eliminar el usuario";

      toast({
        title: "Error",
        description: errorMessage,
        variant: "destructive",
      });
    }
  }
}

// Componente de diálogo para agregar/editar usuario
function UserDialog({
  open,
  onOpenChange,
  user,
  shopId,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  user?: any;
  shopId: string;
}) {
  const queryClient = useQueryClient();
  const isEditing = !!user;

  // ============ VERIFICAR SI ES OWNER EN MODO TIENDA ============
  const isOwnerMode = isOwnerInShopMode();

  const [formData, setFormData] = useState({
    username: user?.username || "",
    password: "",
    fullName: user?.fullName || "",
    email: user?.email || "",
    phone: user?.phone || "",
    role: user?.role || "cashier",
    isActive: user?.isActive ?? true,
  });

  const createMutation = useMutation({
    mutationFn: () =>
      createUser({
        shopId,
        username: formData.username,
        password: formData.password,
        fullName: formData.fullName,
        email: formData.email || undefined,
        phone: formData.phone || undefined,
        role: formData.role as "store_manager" | "cashier",
      }),
    onSuccess: () => {
      toast({
        title: "Usuario creado",
        description: "El usuario ha sido creado correctamente.",
      });
      onOpenChange(false);
      queryClient.invalidateQueries({ queryKey: ["shopUsers", shopId] });
    },
    onError: (error: any) => {
      console.error("Error creando usuario:", error);

      if (error.status === 403) {
        toast({
          title: "⚠️ Límite de usuarios alcanzado",
          description:
            error.message ||
            "Has alcanzado el límite de usuarios para tu plan actual.",
          variant: "destructive",
          duration: 8000,
          action: (
            <div className="mt-2 flex gap-2">
              <Button
                variant="outline"
                size="sm"
                onClick={() => (window.location.href = "/owner/plans")}
                className="bg-purple-50 text-purple-700 border-purple-200 hover:bg-purple-100"
              >
                Mejorar Plan
              </Button>
            </div>
          ),
        });
      } else {
        toast({
          title: "Error",
          description: error.message || "No se pudo crear el usuario",
          variant: "destructive",
        });
      }
    },
  });

  const updateMutation = useMutation({
    mutationFn: () =>
      updateUser(shopId, user.id, {
        username: formData.username,
        fullName: formData.fullName,
        email: formData.email || undefined,
        phone: formData.phone || undefined,
        role: formData.role as "store_manager" | "cashier",
        isActive: formData.isActive,
        ...(formData.password ? { password: formData.password } : {}),
      }),
    onSuccess: () => {
      toast({
        title: "Usuario actualizado",
        description: "Los cambios se han guardado correctamente.",
      });
      onOpenChange(false);
      queryClient.invalidateQueries({ queryKey: ["shopUsers", shopId] });
    },
    onError: (error: any) => {
      console.error("Error actualizando usuario:", error);
      toast({
        title: "Error",
        description: error.message || "No se pudo actualizar el usuario",
        variant: "destructive",
      });
    },
  });

  const handleSubmit = () => {
    if (!formData.username || !formData.fullName) {
      toast({
        title: "Error",
        description: "Usuario y nombre son requeridos",
        variant: "destructive",
      });
      return;
    }

    if (!isEditing && !formData.password) {
      toast({
        title: "Error",
        description: "La contraseña es requerida para nuevos usuarios",
        variant: "destructive",
      });
      return;
    }

    if (isEditing) {
      updateMutation.mutate();
    } else {
      createMutation.mutate();
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[500px]">
        <DialogHeader>
          <DialogTitle>
            {isEditing ? "Editar Usuario" : "Agregar Nuevo Usuario"}
          </DialogTitle>
        </DialogHeader>

        {/* ============ MENSAJE INFORMATIVO PARA OWNER ============ */}
        {isOwnerMode && (
          <div className="bg-purple-50 border border-purple-200 rounded-lg p-3 mb-4">
            <p className="text-sm text-purple-800">
              <strong>👑 Modo Owner:</strong> Tienes permisos totales para
              gestionar todos los usuarios.
            </p>
          </div>
        )}

        <div className="grid gap-4 py-4">
          <div className="grid gap-2">
            <Label htmlFor="username">Usuario *</Label>
            <Input
              id="username"
              value={formData.username}
              onChange={(e) =>
                setFormData({ ...formData, username: e.target.value })
              }
            />
          </div>

          <div className="grid gap-2">
            <Label htmlFor="fullName">Nombre Completo *</Label>
            <Input
              id="fullName"
              value={formData.fullName}
              onChange={(e) =>
                setFormData({ ...formData, fullName: e.target.value })
              }
            />
          </div>

          <div className="grid gap-2">
            <Label htmlFor="password">
              {isEditing
                ? "Nueva Contraseña (dejar en blanco para no cambiar)"
                : "Contraseña *"}
            </Label>
            <Input
              id="password"
              type="password"
              value={formData.password}
              onChange={(e) =>
                setFormData({ ...formData, password: e.target.value })
              }
            />
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div className="grid gap-2">
              <Label htmlFor="email">Email</Label>
              <Input
                id="email"
                type="email"
                value={formData.email}
                onChange={(e) =>
                  setFormData({ ...formData, email: e.target.value })
                }
              />
            </div>
            <div className="grid gap-2">
              <Label htmlFor="phone">Teléfono</Label>
              <Input
                id="phone"
                value={formData.phone}
                onChange={(e) =>
                  setFormData({ ...formData, phone: e.target.value })
                }
              />
            </div>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div className="grid gap-2">
              <Label htmlFor="role">Rol *</Label>
              <Select
                value={formData.role}
                onValueChange={(value) =>
                  setFormData({ ...formData, role: value })
                }
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="cashier">Vendedor</SelectItem>
                  <SelectItem value="store_manager">Store Manager</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div className="grid gap-2">
              <Label htmlFor="isActive">Estado</Label>
              <Select
                value={formData.isActive ? "active" : "inactive"}
                onValueChange={(value) =>
                  setFormData({ ...formData, isActive: value === "active" })
                }
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="active">Activo</SelectItem>
                  <SelectItem value="inactive">Inactivo</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            Cancelar
          </Button>
          <Button
            onClick={handleSubmit}
            disabled={createMutation.isPending || updateMutation.isPending}
          >
            {createMutation.isPending || updateMutation.isPending ? (
              <>
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                Guardando...
              </>
            ) : isEditing ? (
              "Guardar Cambios"
            ) : (
              "Crear Usuario"
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
